import { Fragment, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronUp } from 'lucide-react'
import {
  buildClientProfitabilityRows,
  enrichProjectRowsForProfitabilityFilters,
  filterProjectsCompletedInRange,
  filterProjectsStartedInRange,
  countProjectsMissingStartDate,
  type ActiveTaskEntry,
  type ProjectProfitabilityEntry,
  type TimeLogEntry,
} from './mockData'

type SortDir = 'asc' | 'desc'

type ClientSortKey =
  | 'client'
  | 'projectCount'
  | 'invoiceTotal'
  | 'hoursRecorded'
  | 'costOfTime'
  | 'profit'
  | 'marginPct'
  | 'latestCompletedDate'

type DatePreset = 'all' | 'mtd' | 'custom'

const filterSelectClass =
  'min-w-[12rem] max-w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-dashboard-lavender'

const dateInputClass =
  'rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-dashboard-lavender'

const searchInputClass =
  'min-w-[12rem] max-w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-dashboard-lavender'

function toYyyyMmDd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getMonthToDateRange(): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to.getFullYear(), to.getMonth(), 1)
  return { from: toYyyyMmDd(from), to: toYyyyMmDd(to) }
}

function formatIsoDateDdMmYy(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return iso
  const [, y, mo, d] = m
  return `${d}.${mo}.${y.slice(2)}`
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n)
}

function fmtCurrencyDetailed(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(n)
}

function fmtPct(n: number | null): string {
  if (n == null) return '—'
  return `${Math.round(n * 10) / 10}%`
}

function fmtHours(n: number): string {
  return `${Math.round(n * 10) / 10} h`
}

function includesCI(haystack: string, needle: string): boolean {
  const n = needle.trim().toLowerCase()
  if (!n) return true
  return haystack.toLowerCase().includes(n)
}

function defaultDirForKey(key: ClientSortKey): SortDir {
  if (key === 'client') return 'asc'
  return 'desc'
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <Icon
      className={`h-3.5 w-3.5 shrink-0 ${
        active ? 'text-dashboard-terracotta' : 'text-gray-400 opacity-60 group-hover:opacity-100'
      }`}
      aria-hidden
    />
  )
}

function SortableTh({
  label,
  sortKey,
  currentKey,
  dir,
  align = 'left',
  onSort,
}: {
  label: string
  sortKey: ClientSortKey
  currentKey: ClientSortKey
  dir: SortDir
  align?: 'left' | 'right'
  onSort: (key: ClientSortKey) => void
}) {
  const active = currentKey === sortKey
  return (
    <th
      scope="col"
      className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wider ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`group inline-flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors ${
          align === 'right' ? 'justify-end w-full' : ''
        }`}
      >
        <span>{label}</span>
        <SortIcon active={active} dir={dir} />
      </button>
    </th>
  )
}

function marginClass(marginPct: number | null): string {
  if (marginPct == null) return 'text-gray-700'
  if (marginPct >= 30) return 'text-emerald-700 font-medium'
  if (marginPct >= 0) return 'text-amber-700 font-medium'
  return 'text-red-700 font-medium'
}

function profitClass(profit: number): string {
  if (profit > 0) return 'text-emerald-700'
  if (profit < 0) return 'text-red-700'
  return 'text-gray-900'
}

export function ClientProfitabilitySummary({
  projects,
  completedTasks = [],
  activeTasks = [],
  labourRatePerHour,
}: {
  projects: ProjectProfitabilityEntry[]
  completedTasks?: readonly TimeLogEntry[]
  activeTasks?: readonly ActiveTaskEntry[]
  labourRatePerHour: number
}) {
  const [datePreset, setDatePreset] = useState<DatePreset>('all')
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(() => getMonthToDateRange())
  const [startDatePreset, setStartDatePreset] = useState<DatePreset>('all')
  const [startDateRange, setStartDateRange] = useState<{ from: string; to: string }>(() =>
    getMonthToDateRange()
  )
  const [clientSearch, setClientSearch] = useState('')
  const [expandedClient, setExpandedClient] = useState<string | null>(null)
  const [sort, setSort] = useState<{ key: ClientSortKey; dir: SortDir }>({
    key: 'invoiceTotal',
    dir: 'desc',
  })

  const projectsBeforeStartFilter = useMemo(() => {
    let rows = enrichProjectRowsForProfitabilityFilters(projects, completedTasks ?? [])
    if (datePreset !== 'all') {
      rows = filterProjectsCompletedInRange(rows, dateRange.from, dateRange.to)
    }
    return rows
  }, [projects, completedTasks, datePreset, dateRange.from, dateRange.to])

  const missingStartDateCount = useMemo(
    () => countProjectsMissingStartDate(projectsBeforeStartFilter),
    [projectsBeforeStartFilter]
  )

  const filteredProjects = useMemo(() => {
    let rows = projectsBeforeStartFilter
    if (startDatePreset !== 'all') {
      rows = filterProjectsStartedInRange(rows, startDateRange.from, startDateRange.to)
    }
    return rows
  }, [
    projectsBeforeStartFilter,
    startDatePreset,
    startDateRange.from,
    startDateRange.to,
  ])

  const clientRows = useMemo(
    () =>
      buildClientProfitabilityRows(
        filteredProjects,
        labourRatePerHour,
        completedTasks,
        activeTasks,
        projects
      ),
    [filteredProjects, labourRatePerHour, completedTasks, activeTasks, projects]
  )

  const displayRows = useMemo(() => {
    const filtered = clientRows.filter((row) => includesCI(row.client, clientSearch))
    const { key, dir } = sort
    const d = dir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      let cmp = 0
      switch (key) {
        case 'client':
          cmp = a.client.localeCompare(b.client)
          break
        case 'projectCount':
          cmp = a.projectCount - b.projectCount
          break
        case 'invoiceTotal':
          cmp = a.invoiceTotal - b.invoiceTotal
          break
        case 'hoursRecorded':
          cmp = a.hoursRecorded - b.hoursRecorded
          break
        case 'costOfTime':
          cmp = a.costOfTime - b.costOfTime
          break
        case 'profit':
          cmp = a.profit - b.profit
          break
        case 'marginPct':
          cmp = (a.marginPct ?? -Infinity) - (b.marginPct ?? -Infinity)
          break
        case 'latestCompletedDate':
          cmp = (a.latestCompletedDate ?? '').localeCompare(b.latestCompletedDate ?? '')
          break
      }
      if (cmp === 0) cmp = a.client.localeCompare(b.client)
      return cmp * d
    })
  }, [clientRows, clientSearch, sort])

  const totals = useMemo(() => {
    let projectCount = 0
    let invoiceTotal = 0
    let hoursRecorded = 0
    let costOfTime = 0
    let profit = 0
    for (const row of displayRows) {
      projectCount += row.projectCount
      invoiceTotal += row.invoiceTotal
      hoursRecorded += row.hoursRecorded
      costOfTime += row.costOfTime
      profit += row.profit
    }
    invoiceTotal = Math.round(invoiceTotal * 100) / 100
    hoursRecorded = Math.round(hoursRecorded * 10) / 10
    costOfTime = Math.round(costOfTime * 100) / 100
    profit = Math.round(profit * 100) / 100
    const marginPct =
      invoiceTotal > 0 ? Math.round((profit / invoiceTotal) * 1000) / 10 : null
    return {
      clientCount: displayRows.length,
      projectCount,
      invoiceTotal,
      hoursRecorded,
      costOfTime,
      profit,
      marginPct,
    }
  }, [displayRows])

  const applyDatePreset = (preset: DatePreset) => {
    setDatePreset(preset)
    if (preset === 'mtd') setDateRange(getMonthToDateRange())
    setExpandedClient(null)
  }

  const applyStartDatePreset = (preset: DatePreset) => {
    setStartDatePreset(preset)
    if (preset === 'mtd') setStartDateRange(getMonthToDateRange())
    setExpandedClient(null)
  }

  const setDateFromInput = (which: 'from' | 'to', value: string) => {
    setDatePreset('custom')
    setExpandedClient(null)
    setDateRange((prev) => {
      let from = prev.from
      let to = prev.to
      if (which === 'from') from = value
      else to = value
      if (from > to) {
        if (which === 'from') to = from
        else from = to
      }
      return { from, to }
    })
  }

  const setStartDateFromInput = (which: 'from' | 'to', value: string) => {
    setStartDatePreset('custom')
    setExpandedClient(null)
    setStartDateRange((prev) => {
      let from = prev.from
      let to = prev.to
      if (which === 'from') from = value
      else to = value
      if (from > to) {
        if (which === 'from') to = from
        else from = to
      }
      return { from, to }
    })
  }

  const cycleSort = (key: ClientSortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: defaultDirForKey(key) }
    )
  }

  const toggleExpanded = (client: string) => {
    setExpandedClient((prev) => (prev === client ? null : client))
  }

  const completionFilterLabel =
    datePreset === 'all'
      ? ''
      : ` · completed ${formatIsoDateDdMmYy(dateRange.from)} – ${formatIsoDateDdMmYy(dateRange.to)}`
  const startFilterLabel =
    startDatePreset === 'all'
      ? ''
      : ` · started ${formatIsoDateDdMmYy(startDateRange.from)} – ${formatIsoDateDdMmYy(startDateRange.to)}`

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-center gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="client-profitability-search" className="text-xs font-medium text-gray-600">
            Search client
          </label>
          <input
            id="client-profitability-search"
            type="search"
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            placeholder="Filter by name…"
            className={searchInputClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="client-profitability-date-preset" className="text-xs font-medium text-gray-600">
            Completion date
          </label>
          <select
            id="client-profitability-date-preset"
            value={datePreset}
            onChange={(e) => applyDatePreset(e.target.value as DatePreset)}
            className={filterSelectClass}
          >
            <option value="all">All dates</option>
            <option value="mtd">Month to date</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        {datePreset !== 'all' ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="client-profitability-date-from" className="text-xs font-medium text-gray-600">
                From
              </label>
              <input
                id="client-profitability-date-from"
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateFromInput('from', e.target.value)}
                className={dateInputClass}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="client-profitability-date-to" className="text-xs font-medium text-gray-600">
                To
              </label>
              <input
                id="client-profitability-date-to"
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateFromInput('to', e.target.value)}
                className={dateInputClass}
              />
            </div>
          </div>
        ) : null}
        <div className="flex flex-col gap-1">
          <label htmlFor="client-profitability-start-date-preset" className="text-xs font-medium text-gray-600">
            Project start date
          </label>
          <select
            id="client-profitability-start-date-preset"
            value={startDatePreset}
            onChange={(e) => applyStartDatePreset(e.target.value as DatePreset)}
            className={filterSelectClass}
          >
            <option value="all">All dates</option>
            <option value="mtd">Month to date</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        {startDatePreset !== 'all' ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="client-profitability-start-from" className="text-xs font-medium text-gray-600">
                Start from
              </label>
              <input
                id="client-profitability-start-from"
                type="date"
                value={startDateRange.from}
                onChange={(e) => setStartDateFromInput('from', e.target.value)}
                className={dateInputClass}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="client-profitability-start-to" className="text-xs font-medium text-gray-600">
                Start to
              </label>
              <input
                id="client-profitability-start-to"
                type="date"
                value={startDateRange.to}
                onChange={(e) => setStartDateFromInput('to', e.target.value)}
                className={dateInputClass}
              />
            </div>
          </div>
        ) : null}
      </div>

      <p className="text-center text-xs text-gray-500 mb-3 tabular-nums">
        {totals.clientCount} client{totals.clientCount === 1 ? '' : 's'} · {totals.projectCount} project
        {totals.projectCount === 1 ? '' : 's'}
        {clientSearch.trim() ? ` matching “${clientSearch.trim()}”` : ''}
        {completionFilterLabel}
        {startFilterLabel}
      </p>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="w-8 px-2 py-2.5" aria-label="Expand" />
              <SortableTh
                label="Client"
                sortKey="client"
                currentKey={sort.key}
                dir={sort.dir}
                onSort={cycleSort}
              />
              <SortableTh
                label="Projects"
                sortKey="projectCount"
                currentKey={sort.key}
                dir={sort.dir}
                align="right"
                onSort={cycleSort}
              />
              <SortableTh
                label="Invoiced (ex GST)"
                sortKey="invoiceTotal"
                currentKey={sort.key}
                dir={sort.dir}
                align="right"
                onSort={cycleSort}
              />
              <SortableTh
                label="Hours tracked"
                sortKey="hoursRecorded"
                currentKey={sort.key}
                dir={sort.dir}
                align="right"
                onSort={cycleSort}
              />
              <SortableTh
                label="Cost of time"
                sortKey="costOfTime"
                currentKey={sort.key}
                dir={sort.dir}
                align="right"
                onSort={cycleSort}
              />
              <SortableTh
                label="Profit"
                sortKey="profit"
                currentKey={sort.key}
                dir={sort.dir}
                align="right"
                onSort={cycleSort}
              />
              <SortableTh
                label="Margin"
                sortKey="marginPct"
                currentKey={sort.key}
                dir={sort.dir}
                align="right"
                onSort={cycleSort}
              />
              <SortableTh
                label="Latest completed"
                sortKey="latestCompletedDate"
                currentKey={sort.key}
                dir={sort.dir}
                align="right"
                onSort={cycleSort}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {displayRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">
                  <p>No clients match the current filters.</p>
                  {startDatePreset !== 'all' && missingStartDateCount > 0 ? (
                    <p className="mt-1 text-xs text-gray-400">
                      {missingStartDateCount} project{missingStartDateCount === 1 ? '' : 's'} in
                      this completion range have no project start date in PWF — run Fetch from PWF
                      to refresh.
                    </p>
                  ) : null}
                  {datePreset !== 'all' && startDatePreset !== 'all' ? (
                    <p className="mt-1 text-xs text-gray-400">
                      Completion and start date filters both apply (project must match both ranges).
                    </p>
                  ) : null}
                </td>
              </tr>
            ) : (
              displayRows.map((row) => {
                const isExpanded = expandedClient === row.client
                return (
                  <Fragment key={row.client}>
                    <tr className="hover:bg-gray-50/80">
                      <td className="px-2 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(row.client)}
                          className="inline-flex items-center justify-center rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? 'Hide' : 'Show'} projects for ${row.client}`}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" aria-hidden />
                          ) : (
                            <ChevronDown className="h-4 w-4" aria-hidden />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-2.5 font-medium text-gray-900">{row.client}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums">
                        {row.projectCount}
                        {row.largeProjectCount + row.transactionalProjectCount > 0 ? (
                          <span className="block text-[10px] text-gray-400 font-normal">
                            {row.largeProjectCount} large · {row.transactionalProjectCount} trans.
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-900 tabular-nums font-medium">
                        {fmtCurrency(row.invoiceTotal)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums">
                        {fmtHours(row.hoursRecorded)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums">
                        {fmtCurrencyDetailed(row.costOfTime)}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-medium ${profitClass(row.profit)}`}
                      >
                        {fmtCurrencyDetailed(row.profit)}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums ${marginClass(row.marginPct)}`}
                      >
                        {fmtPct(row.marginPct)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums text-xs">
                        {row.latestCompletedDate
                          ? formatIsoDateDdMmYy(row.latestCompletedDate)
                          : '—'}
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-3 bg-gray-50/90 border-t border-gray-100">
                          <p className="text-xs font-medium text-gray-600 mb-2">
                            Projects for {row.client}
                          </p>
                          <div className="overflow-x-auto rounded border border-gray-200 bg-white">
                            <table className="min-w-full text-xs">
                              <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium">Project</th>
                                  <th className="px-3 py-2 text-left font-medium">Type</th>
                                  <th className="px-3 py-2 text-right font-medium">Invoiced (ex GST)</th>
                                  <th className="px-3 py-2 text-right font-medium">Hours</th>
                                  <th className="px-3 py-2 text-right font-medium">Profit</th>
                                  <th className="px-3 py-2 text-right font-medium">Margin</th>
                                  <th className="px-3 py-2 text-right font-medium">Started</th>
                                  <th className="px-3 py-2 text-right font-medium">Completed</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {row.projects.map((project) => (
                                  <tr key={project.id}>
                                    <td className="px-3 py-2 text-gray-900">
                                      <span className="font-mono font-semibold tabular-nums">
                                        {project.projectNumber}
                                      </span>
                                      {project.projectTitle ? (
                                        <span className="block text-gray-600 font-normal truncate max-w-xs">
                                          {project.projectTitle}
                                        </span>
                                      ) : null}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600">
                                      {project.projectType ?? '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                                      {fmtCurrencyDetailed(project.invoiceTotal)}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                                      {fmtHours(project.hoursRecorded)}
                                    </td>
                                    <td
                                      className={`px-3 py-2 text-right tabular-nums ${profitClass(project.profit)}`}
                                    >
                                      {fmtCurrencyDetailed(project.profit)}
                                    </td>
                                    <td
                                      className={`px-3 py-2 text-right tabular-nums ${marginClass(project.marginPct)}`}
                                    >
                                      {fmtPct(project.marginPct)}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                                      {project.startDate
                                        ? formatIsoDateDdMmYy(project.startDate)
                                        : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                                      {project.completedDate
                                        ? formatIsoDateDdMmYy(project.completedDate)
                                        : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })
            )}
          </tbody>
          {displayRows.length > 0 ? (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
              <tr className="font-semibold text-gray-900">
                <td className="px-2 py-2.5" />
                <td className="px-4 py-2.5">
                  Total
                  <span className="block text-[10px] font-normal text-gray-500">
                    {totals.clientCount} client{totals.clientCount === 1 ? '' : 's'}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{totals.projectCount}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{fmtCurrency(totals.invoiceTotal)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{fmtHours(totals.hoursRecorded)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {fmtCurrencyDetailed(totals.costOfTime)}
                </td>
                <td className={`px-4 py-2.5 text-right tabular-nums ${profitClass(totals.profit)}`}>
                  {fmtCurrencyDetailed(totals.profit)}
                </td>
                <td className={`px-4 py-2.5 text-right tabular-nums ${marginClass(totals.marginPct)}`}>
                  {fmtPct(totals.marginPct)}
                </td>
                <td className="px-4 py-2.5" />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <p className="text-center text-xs text-gray-400 mt-3">
        Margin = (invoiced − tracked hours × ${labourRatePerHour}/hr) ÷ invoiced, rolled up per client.
        Expand a row to see contributing projects.
      </p>
    </div>
  )
}
